import { useState } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useReviewStore } from '../../stores/review';
import Chip from '../chip';
import useUserMemory from '../../hooks/use-user-memory';
import MemoryButton from '../memory-button';
import { reviewService } from '../../queries/review';
import { CardFront, CardBack } from '../card-review';

export default function FlashcardReview({
  onFinish,
  isModal,
  onChangeFaceOfView,
}: {
  onFinish: () => Promise<boolean> | void;
  isModal?: boolean;
  onChangeFaceOfView?: (faceOfView: 'front' | 'back') => void;
}) {
  const {
    currentFlashcard,
    qtdFlashcardsReviewed,
    qtdFlashcards,
    setNextFlashcard,
  } = useReviewStore();

  const { memoryUserUuid } = useUserMemory();
  const isLastFlashcard = qtdFlashcardsReviewed === qtdFlashcards;

  const [difficulty, setDifficulty] = useState<
    'easy' | 'medium' | 'hard' | null
  >(null);
  const [flipped, setFlipped] = useState(false);

  const handleChangeDifficulty = async (
    difficulty: 'easy' | 'medium' | 'hard'
  ) => {
    setDifficulty(difficulty);

    if (isModal) {
      const response = await onFinish?.();

      if (!response) return;

      handleSaveAnswer();
      setDifficulty(null);
      setFlipped(false);
    }
  };

  const handleSaveAnswer = async () => {
    if (!memoryUserUuid || !currentFlashcard?.flashcardDeckUuid || !difficulty)
      return;

    await reviewService.saveAnswerFlashcard(
      memoryUserUuid,
      currentFlashcard?.flashcardDeckUuid,
      difficulty
    );
  };

  const handleClickNext = () => {
    handleSaveAnswer();

    if (isLastFlashcard) {
      onFinish();
    } else {
      setNextFlashcard();
      setDifficulty(null);
      setFlipped(false);
    }
  };

  const handleFlipCard = (flipped: boolean) => {
    setFlipped(flipped);
    onChangeFaceOfView?.(flipped ? 'back' : 'front');
  };

  const deckName = currentFlashcard?.deckName;
  return (
    <div
      className={`flex ${
        isModal ? '' : 'h-screen'
      } w-full max-w-[748px] flex-col items-center justify-center gap-4`}
    >
      {deckName && (
        <Typography className='text-h4 font-bold text-mono-color-darkest'>
          {deckName}
        </Typography>
      )}
      <div className='flex w-full flex-col'>
        <Typography className={`text-h4 font-bold text-mono-color-darkest `}>
          {flipped ? 'Verso' : 'Frente'}
        </Typography>
      </div>
      {flipped && (
        <div className='flex w-full flex-col items-center justify-center'>
          <Typography className='text-sm font-bold text-mono-color-dark'>
            {currentFlashcard?.front}
          </Typography>
        </div>
      )}
      <div className='relative h-48 w-full [perspective:1000px]'>
        <div
          className={`relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d] ${
            flipped ? '[transform:rotateY(180deg)]' : ''
          }`}
        >
          <CardFront setFlipped={handleFlipCard} />
          <CardBack setFlipped={handleFlipCard} />
        </div>
      </div>

      {flipped && (
        <>
          <div className='flex w-full flex-row gap-4'>
            <Chip
              label='Fácil'
              defaultIcon
              className='w-full'
              variant='easy'
              onClick={() => handleChangeDifficulty('easy')}
              type={difficulty === 'easy' ? 'filled' : 'outline'}
            />
            <Chip
              label='Médio'
              defaultIcon
              className='w-full'
              variant='medium'
              onClick={() => handleChangeDifficulty('medium')}
              type={difficulty === 'medium' ? 'filled' : 'outline'}
            />
            <Chip
              label='Difícil'
              defaultIcon
              className='w-full'
              variant='hard'
              onClick={() => handleChangeDifficulty('hard')}
              type={difficulty === 'hard' ? 'filled' : 'outline'}
            />
          </div>
          {!isModal && (
            <div className='mt-8 flex w-full justify-center'>
              <MemoryButton
                label={isLastFlashcard ? 'Finalizar' : 'Próximo'}
                variant='primary'
                onClick={handleClickNext}
                className='w-[236px]'
                disabled={!difficulty}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
